import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

function PropertyMaintenance({ propertyId }) {
    const [maintenanceRequests, setMaintenanceRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newRequest, setNewRequest] = useState({
        property_id: propertyId,
        tenant_id: null,
        unit_number: '',
        request_type: '',
        priority: 'medium',
        description: '',
        scheduled_date: '',
        assigned_to: '',
        estimated_cost: '',
        notes: ''
    });
    const [tenants, setTenants] = useState([]);

    useEffect(() => {
        if (propertyId) {
            fetchMaintenanceRequests();
            fetchTenants();
        }
    }, [propertyId]);

    const fetchMaintenanceRequests = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/maintenance-requests?property_id=${propertyId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch maintenance requests');
            }
            const data = await response.json();
            setMaintenanceRequests(data);
            setError(null);
        } catch (err) {
            console.error("Error fetching property maintenance requests:", err);
            setError("Failed to fetch maintenance requests.");
        } finally {
            setLoading(false);
        }
    };

    const fetchTenants = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/tenants`);
            if (!response.ok) {
                throw new Error('Failed to fetch tenants');
            }
            const data = await response.json();
            setTenants(data);
        } catch (err) {
            console.error("Error fetching tenants:", err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewRequest(prev => ({ ...prev, [name]: value }));
    };

    const handleAddRequest = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_BASE_URL}/api/maintenance-requests`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newRequest)
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add request');
            }
            
            alert('Maintenance request added successfully!');
            setShowAddModal(false);
            setNewRequest({
                property_id: propertyId,
                tenant_id: null,
                unit_number: '',
                request_type: '',
                priority: 'medium',
                description: '',
                scheduled_date: '',
                assigned_to: '',
                estimated_cost: '',
                notes: ''
            });
            fetchMaintenanceRequests();
        } catch (err) {
            console.error("Error adding maintenance request:", err);
            alert(`Failed to add request: ${err.message}`);
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority.toLowerCase()) {
            case 'high': return '#d32f2f';
            case 'medium': return '#f57c00';
            case 'low': return '#388e3c';
            default: return '#666';
        }
    };

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'pending': return '#1976d2';
            case 'in-progress': return '#f57c00';
            case 'completed': return '#388e3c';
            case 'cancelled': return '#6c757d';
            default: return '#666';
        }
    };

    if (loading) return (
        <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading maintenance requests...</p>
        </div>
    );
    if (error) return <div className="error-message">{error}</div>;

    return (
        <div className="info-section">
            <div className="section-header">
                <h2>Maintenance Requests</h2>
                <button className="notion-button" onClick={() => setShowAddModal(true)}>Add Request</button>
            </div>

            {maintenanceRequests.length === 0 ? (
                <p className="empty-state">No maintenance requests for this property.</p>
            ) : (
                <div className="requests-grid">
                    {maintenanceRequests.map(request => (
                        <div key={request.id} className="request-card">
                            <div className="request-header">
                                <h3>{request.request_type}</h3>
                                <span className="priority-badge" style={{ backgroundColor: getPriorityColor(request.priority), color: 'white' }}>
                                    {request.priority}
                                </span>
                            </div>
                            <p className="unit-number">Unit: {request.unit_number || 'N/A'}</p>
                            <p className="tenant-name">Tenant: {request.tenant_name || 'N/A'}</p>
                            <p className="description">{request.description}</p>
                            <div className="request-footer">
                                <span>Status: <span className="status-badge" style={{ backgroundColor: getStatusColor(request.status), color: 'white' }}>{request.status}</span></span>
                                <span>Created: {new Date(request.created_at).toLocaleDateString()}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showAddModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2 className="notion-modal-title">Add New Maintenance Request</h2>
                        <form onSubmit={handleAddRequest}>
                            <div className="notion-modal-field">
                                <label>Tenant (Optional)</label>
                                <select
                                    name="tenant_id"
                                    value={newRequest.tenant_id || ''}
                                    onChange={handleInputChange}
                                    className="notion-input"
                                >
                                    <option value="">Select Tenant (Optional)</option>
                                    {tenants.map(tenant => (
                                        <option key={tenant.id} value={tenant.id}>{tenant.first_name} {tenant.last_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="notion-modal-field">
                                <label>Unit Number</label>
                                <input
                                    type="text"
                                    name="unit_number"
                                    value={newRequest.unit_number}
                                    onChange={handleInputChange}
                                    className="notion-input"
                                />
                            </div>
                            <div className="notion-modal-field">
                                <label>Request Type *</label>
                                <input
                                    type="text"
                                    name="request_type"
                                    value={newRequest.request_type}
                                    onChange={handleInputChange}
                                    className="notion-input"
                                    required
                                />
                            </div>
                            <div className="notion-modal-field">
                                <label>Priority *</label>
                                <select
                                    name="priority"
                                    value={newRequest.priority}
                                    onChange={handleInputChange}
                                    className="notion-input"
                                    required
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </div>
                            <div className="notion-modal-field">
                                <label>Description *</label>
                                <textarea
                                    name="description"
                                    value={newRequest.description}
                                    onChange={handleInputChange}
                                    className="notion-input"
                                    required
                                ></textarea>
                            </div>
                            <div className="notion-modal-field">
                                <label>Scheduled Date</label>
                                <input
                                    type="date"
                                    name="scheduled_date"
                                    value={newRequest.scheduled_date}
                                    onChange={handleInputChange}
                                    className="notion-input"
                                />
                            </div>
                            <div className="notion-modal-field">
                                <label>Assigned To</label>
                                <input
                                    type="text"
                                    name="assigned_to"
                                    value={newRequest.assigned_to}
                                    onChange={handleInputChange}
                                    className="notion-input"
                                />
                            </div>
                            <div className="notion-modal-field">
                                <label>Estimated Cost</label>
                                <input
                                    type="number"
                                    name="estimated_cost"
                                    value={newRequest.estimated_cost}
                                    onChange={handleInputChange}
                                    className="notion-input"
                                />
                            </div>
                            <div className="notion-modal-field">
                                <label>Notes</label>
                                <textarea
                                    name="notes"
                                    value={newRequest.notes}
                                    onChange={handleInputChange}
                                    className="notion-input"
                                ></textarea>
                            </div>
                            <div className="notion-modal-actions">
                                <button type="button" className="cancel-button" onClick={() => setShowAddModal(false)}>Cancel</button>
                                <button type="submit" className="notion-button">Add Request</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PropertyMaintenance; 